import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { Thread } from "../../data/messages"
import apiClient from "../../api/apiClient"

interface MessagesState {
  threads: Thread[]
  observedThreads: Thread[]
  recipients: { id: string; name: string; role: string; building?: string; roomNumber?: string }[]
  isLoading: boolean
  error: string | null
}

const initialState: MessagesState = {
  threads: [],
  observedThreads: [],
  recipients: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchThreads = createAsyncThunk(
  "messages/fetchThreads",
  async (params: { observedFrom?: string } | undefined, { rejectWithValue }) => {
    try {
      const queryParams = params?.observedFrom ? `?observedFrom=${params.observedFrom}` : ""
      const response = await apiClient.get(`/messages/threads${queryParams}`)
      return { threads: response.data.threads, observedThreads: response.data.observedThreads || [] }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch threads")
    }
  },
)

export const fetchThreadById = createAsyncThunk(
  "messages/fetchThreadById",
  async (threadId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/messages/threads/${threadId}`)
      return response.data.thread
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch thread")
    }
  },
)

export const fetchRecipients = createAsyncThunk("messages/fetchRecipients", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get("/messages/recipients")
    return response.data.recipients
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || "Failed to fetch recipients")
  }
})

export const markThreadAsRead = createAsyncThunk(
  "messages/markThreadAsRead",
  async (threadId: string, { rejectWithValue }) => {
    try {
      await apiClient.put(`/messages/threads/${threadId}/read`)
      return threadId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to mark thread as read")
    }
  },
)

export const sendMessage = createAsyncThunk(
  "messages/sendMessage",
  async (
    { threadId, content, attachment }: { threadId: string; content: string; attachment?: File | null },
    { rejectWithValue },
  ) => {
    try {
      let response

      if (attachment) {
        // Create FormData for file upload
        const formData = new FormData()
        formData.append("content", content)
        formData.append("attachment", attachment)
        formData.append("attachmentName", attachment.name)
        formData.append("attachmentType", attachment.type)

        response = await apiClient.post(`/messages/threads/${threadId}/messages`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
      } else {
        // Regular JSON request
        response = await apiClient.post(`/messages/threads/${threadId}/messages`, { content })
      }

      return { threadId, message: response.data.messageData }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to send message")
    }
  },
)

export const createThread = createAsyncThunk(
  "messages/createThread",
  async (
    { recipientId, content, attachment }: { recipientId: string; content: string; attachment?: File | null },
    { rejectWithValue },
  ) => {
    try {
      let response

      if (attachment) {
        // Create FormData for file upload
        const formData = new FormData()
        formData.append("recipientId", recipientId)
        formData.append("message", content)
        formData.append("attachment", attachment)
        formData.append("attachmentName", attachment.name)
        formData.append("attachmentType", attachment.type)

        response = await apiClient.post("/messages/threads", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
      } else {
        // Regular JSON request
        response = await apiClient.post("/messages/threads", { recipientId, message: content })
      }

      return { success: true, threadId: response.data.threadId }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to create thread")
    }
  },
)

export const deleteThread = createAsyncThunk("messages/deleteThread", async (threadId: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/messages/threads/${threadId}`)
    return { success: true, threadId }
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || "Failed to delete thread")
  }
})

export const createBuildingChat = createAsyncThunk(
  "messages/createBuildingChat",
  async (
    { buildingId, message, attachment }: { buildingId: string; message: string; attachment?: File | null },
    { rejectWithValue },
  ) => {
    try {
      let response

      if (attachment) {
        // Create FormData for file upload
        const formData = new FormData()
        formData.append("buildingId", buildingId)
        formData.append("message", message)
        formData.append("attachment", attachment)
        formData.append("attachmentName", attachment.name)
        formData.append("attachmentType", attachment.type)

        response = await apiClient.post("/messages/building-chat", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
      } else {
        // Regular JSON request
        response = await apiClient.post("/messages/building-chat", { buildingId, message })
      }

      return { success: true, threadId: response.data.threadId }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to create building chat")
    }
  },
)

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch threads
      .addCase(fetchThreads.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchThreads.fulfilled, (state, action: PayloadAction<{ threads: Thread[]; observedThreads: Thread[] }>) => {
        const sortedThreads = action.payload.threads.sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1
          if (!a.isPinned && b.isPinned) return 1
          if (a.type === "building-wide" && b.type !== "building-wide") return -1
          if (a.type !== "building-wide" && b.type === "building-wide") return 1
          return new Date(b.lastMessageDate).getTime() - new Date(a.lastMessageDate).getTime()
        })
        state.threads = sortedThreads
        state.observedThreads = action.payload.observedThreads
        state.isLoading = false
      })
      .addCase(fetchThreads.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Fetch thread by ID
      .addCase(fetchThreadById.fulfilled, (state, action: PayloadAction<Thread>) => {
        if (action.payload.isObserver) {
          const index = state.observedThreads.findIndex((t) => t.id === action.payload.id)
          if (index !== -1) {
            state.observedThreads[index] = action.payload
          } else {
            state.observedThreads.push(action.payload)
          }
        } else {
          const index = state.threads.findIndex((t) => t.id === action.payload.id)
          if (index !== -1) {
            state.threads[index] = action.payload
          } else {
            state.threads.push(action.payload)
          }
        }
      })
      // Fetch recipients
      .addCase(fetchRecipients.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(
        fetchRecipients.fulfilled,
        (
          state,
          action: PayloadAction<{ id: string; name: string; role: string; building?: string; roomNumber?: string }[]>,
        ) => {
          state.recipients = action.payload
          state.isLoading = false
        },
      )
      .addCase(fetchRecipients.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Mark thread as read
      .addCase(markThreadAsRead.fulfilled, (state, action: PayloadAction<string>) => {
        const thread = state.threads.find((t) => t.id === action.payload)
        if (thread) {
          thread.unread = false
        }
        const observedThread = state.observedThreads.find((t) => t.id === action.payload)
        if (observedThread) {
          observedThread.unread = false
        }
      })
      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { threadId, message } = action.payload
        const thread = state.threads.find((t) => t.id === threadId)
        if (thread) {
          thread.messages.push(message)
          if (message.hasAttachment) {
            thread.lastMessage = message.content
              ? `${message.content} [Attachment: ${message.attachmentName}]`
              : `[Attachment: ${message.attachmentName}]`
          } else {
            thread.lastMessage = message.content
          }
          thread.lastMessageDate = message.timestamp
        }
        state.isLoading = false
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Create thread
      .addCase(createThread.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createThread.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(createThread.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete thread
      .addCase(deleteThread.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteThread.fulfilled, (state, action) => {
        const { threadId } = action.payload
        state.threads = state.threads.filter((thread) => thread.id !== threadId)
        state.observedThreads = state.observedThreads.filter((thread) => thread.id !== threadId)
        state.isLoading = false
      })
      .addCase(deleteThread.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(createBuildingChat.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createBuildingChat.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(createBuildingChat.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export default messagesSlice.reducer
